import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { getDateRange, validateDateRange } from '../utils/dateHelpers';
import { useAuth } from '../components/auth/AuthContext';
import { useUserRole } from '../hooks/useUserRole';
import { usePermissions } from '../hooks/usePermissions';
import { canViewAllData, hasPermission, canViewAllReports, FEATURE_PERMISSIONS, getRbacMatchNames } from '../utils/permissions';
import { getValueRangeMidpoint } from '../components/forms/OppModal';
import DateRangeSelector from '../components/common/DateRangeSelector';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import ExportButton from '../components/common/ExportButton';
import { exportData } from '../utils/exportUtils';

const ReportsDashboard = ({ user }) => {
    const { currentUser } = useAuth();
    const userRole = useUserRole(user);
    const { can } = usePermissions(user);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [range, setRange] = useState('30');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [cacheRefreshing, setCacheRefreshing] = useState(false);
    const [refreshProgress, setRefreshProgress] = useState(0);
    const [cacheAge, setCacheAge] = useState(null);
    const [lastCacheUpdate, setLastCacheUpdate] = useState(null);
    const [cacheRefreshCooldown, setCacheRefreshCooldown] = useState(12); // Default 12 hours
    const refreshTimerRef = useRef(null);
    const fetchInProgressRef = useRef(false); // Prevent duplicate fetches

    // Fetch cache refresh cooldown from system settings
    useEffect(() => {
        const fetchCooldown = async () => {
            try {
                const settingDoc = await getDoc(doc(db, collectionPath('systemSettings'), 'cacheRefreshCooldownHours'));
                if (settingDoc.exists() && settingDoc.data().value) {
                    setCacheRefreshCooldown(Number(settingDoc.data().value));
                }
            } catch (error) {
                // Silently fail - keep default value of 12 hours
                // This is a non-critical setting; permission errors shouldn't break the page
                if (error.code !== 'permission-denied') {
                    console.warn('[ReportsDashboard] Could not fetch cache cooldown setting:', error.message);
                }
            }
        };
        fetchCooldown();
    }, []);

    // Cleanup progress timer on unmount
    useEffect(() => {
        return () => {
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
            }
        };
    }, []);

    // Handle cache refresh (admin only)
    const handleRefreshCache = async () => {
        if (!hasPermission(user, FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH)) {
            setToast({ message: 'Only admins can refresh the cache', type: 'error' });
            return;
        }

        // Check if cache was updated within cooldown period
        if (lastCacheUpdate) {
            const hoursSinceUpdate = (Date.now() - lastCacheUpdate.getTime()) / (1000 * 60 * 60);
            if (hoursSinceUpdate < cacheRefreshCooldown) {
                const hoursRemaining = Math.ceil(cacheRefreshCooldown - hoursSinceUpdate);
                setToast({ 
                    message: `Cache was updated ${Math.round(hoursSinceUpdate * 10) / 10}h ago. Please wait ${hoursRemaining}h before refreshing again.`, 
                    type: 'error' 
                });
                return;
            }
        }

        const confirmed = window.confirm(
            'This will refresh all reports cache (summary, comparison, and inactivity). ' +
            'It may take 2-3 minutes. Continue?'
        );
        
        if (!confirmed) return;

        try {
            setCacheRefreshing(true);
            setRefreshProgress(0);
            
            // Start progress timer (simulate progress)
            let progress = 0;
            refreshTimerRef.current = setInterval(() => {
                progress += 1;
                if (progress <= 90) { // Cap at 90% until actual completion
                    setRefreshProgress(progress);
                }
            }, 1000); // Update every second
            
            setToast({ message: 'Refreshing all caches... This may take 2-3 minutes.', type: 'info' });
            
            // Call the main reports cache refresh
            console.log('[ReportsDashboard] Calling refreshReportsCache function...');
            const refreshCacheFunc = httpsCallable(functions, 'refreshReportsCache');
            await refreshCacheFunc();
            
            // Also call the inactivity cache refresh
            console.log('[ReportsDashboard] Calling generateInactivityCache function...');
            const generateInactivityCacheFunc = httpsCallable(functions, 'generateInactivityCache');
            await generateInactivityCacheFunc({});
            
            // Clear progress timer
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            
            setRefreshProgress(100);
            setToast({ message: 'All caches refreshed successfully! Reloading...', type: 'success' });
            
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        } catch (error) {
            // Clear progress timer
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            
            console.error('[ReportsDashboard] Error refreshing cache:', error);
            setCacheRefreshing(false);
            setRefreshProgress(0);
            
            let errorMessage = 'Error refreshing cache';
            if (error.code === 'unauthenticated') {
                errorMessage = 'Please sign in to refresh the cache';
            } else if (error.code === 'permission-denied') {
                errorMessage = 'Only admins can refresh the cache';
            } else if (error.message) {
                errorMessage = `Error: ${error.message}`;
            }
            
            setToast({ message: errorMessage, type: 'error' });
        }
    };

    useEffect(() => {
        // Don't fetch until we have both user AND userRole loaded
        if (!user || !user.uid) return;
        if (userRole === undefined || userRole === null) return; // Wait for role to load
        
        // Prevent duplicate fetches (React strict mode, rapid re-renders)
        if (fetchInProgressRef.current) {
            console.log('[ReportsDashboard] Fetch already in progress, skipping...');
            return;
        }

        const fetchSummaryData = async () => {
            fetchInProgressRef.current = true;
            setLoading(true);
            try {
                
                // Validate date range before fetching
                const validation = validateDateRange(range, customStart, customEnd);
                if (!validation.isValid) {
                    setToast({ message: validation.error, type: 'error' });
                    setLoading(false);
                    return;
                }

                // Determine cache ID based on range
                const isStandardRange = ['7', '14', '30', '90', '180', '365', 'ytd'].includes(range);
                const cacheId = range === 'ytd' 
                    ? 'summary_ytd' 
                    : isStandardRange 
                        ? `summary_${range.padStart(2, '0')}d` 
                        : null;
                
                // Cache stores only TOP 100 orders by $ amount (Firestore 1MB doc limit).
                // Non-admin users MUST bypass cache — filtering the top-100 list drops their
                // smaller orders and produces inaccurate revenue/order totals that don't match
                // SalesRepReports (which uses pre-aggregated repPerformance).
                const canUseCache = canViewAllReports(user) || can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL);
                
                // Try to fetch cached summary for standard ranges (admin/view-all users only)
                if (cacheId && canUseCache) {
                    // Also fetch cache age from any standard cache to show last update time
                    const anyCacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), 'summary_30d'));
                    if (anyCacheDoc.exists() && anyCacheDoc.data().lastUpdated) {
                        const lastUpdated = anyCacheDoc.data().lastUpdated.toDate();
                        const ageMinutes = Math.round((Date.now() - lastUpdated.getTime()) / (1000 * 60));
                        setCacheAge(ageMinutes);
                        setLastCacheUpdate(lastUpdated);
                    }
                    try {
                        const cacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), cacheId));
                        
                        if (cacheDoc.exists()) {
                            const data = cacheDoc.data();
                            const now = new Date();
                            const lastUpdated = data.lastUpdated?.toDate?.() || new Date(0);
                            const ageMinutes = Math.floor((now - lastUpdated) / (1000 * 60));
                                                        
                            // Use cache if less than 12 hours old (generation runs every 8 hours)
                            if (ageMinutes < 720 && data.summary) {
                                console.log(`[ReportsDashboard] ✅ Using cached data (${ageMinutes} min old, cache ID: ${cacheId})`);
                                // RBAC: For non-admin users, filter cached orders and show filtered metrics
                                if (!canViewAllReports(user) && !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && data.ordersForRepFilter && user?.displayName) {
                                    console.log('[ReportsDashboard] Non-admin user - filtering cached data');
                                    
                                    // Get date range for filtering
                                    const { start, end } = getDateRange(range, customStart, customEnd);
                                    
                                    // Filter cached orders by commissions AND date range (multi-name matching)
                                    const matchNames = getRbacMatchNames(user);
                                    const matchSet = new Set(matchNames);
                                    let filteredOrders = data.ordersForRepFilter.filter(order => {
                                        // Check if assigned to user (multi-name matching for impersonation support)
                                        if (!order.commissions_displayNames?.some(name => matchSet.has(name.toLowerCase().trim()))) {
                                            return false;
                                        }
                                        
                                        // Check date range
                                        const orderDate = order.creation_time 
                                            ? new Date(order.creation_time.replace(' ', 'T'))
                                            : order.createdAt?.toDate?.() 
                                                ? order.createdAt.toDate() 
                                                : null;
                                        
                                        if (!orderDate) return false;
                                        return orderDate >= start && orderDate <= end;
                                    });
                                    
                                    console.log('[ReportsDashboard] Cached data filtered:', {
                                        totalCached: data.ordersForRepFilter.length,
                                        filtered: filteredOrders.length,
                                        matchNames,
                                        dateRange: range
                                    });
                                    
                                    // Recalculate metrics from filtered orders
                                    const totalRevenue = filteredOrders.reduce((sum, order) => 
                                        sum + (Number(order.total) || Number(order.amount) || 0), 0
                                    );
                                    const totalOrders = filteredOrders.length;
                                    
                                    // Fetch user's opportunities for pipeline/win rate metrics
                                    const oppsSnap = await getDocs(collection(db, collectionPath('opportunities')));
                                    let userOpps = oppsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                                    
                                    // RBAC: filter to user's opportunities
                                    userOpps = userOpps.filter(opp => opp.uid === user.uid);
                                    
                                    // Date range filter
                                    const filteredUserOpps = userOpps.filter(opp => {
                                        const dateField = opp.createdAt || opp.startDate;
                                        const oppDate = dateField?.toDate?.()
                                            ? dateField.toDate()
                                            : dateField ? new Date(dateField) : null;
                                        if (!oppDate || isNaN(oppDate.getTime())) return false;
                                        return oppDate >= start && oppDate <= end;
                                    });
                                    
                                    // Compute opportunity metrics
                                    const activeUserOpps = filteredUserOpps.filter(opp => !['Closed Won', 'Closed Lost'].includes(opp.stage));
                                    const pipelineValue = activeUserOpps.reduce((sum, opp) => sum + getValueRangeMidpoint(opp.value), 0);
                                    const wonOpps = filteredUserOpps.filter(opp => opp.stage === 'Closed Won').length;
                                    const lostOpps = filteredUserOpps.filter(opp => opp.stage === 'Closed Lost').length;
                                    const closedOpps = wonOpps + lostOpps;
                                    const winRate = closedOpps > 0 ? (wonOpps / closedOpps) : 0;
                                    
                                    setSummaryData({
                                        totalRevenue,
                                        totalOrders,
                                        pipelineValue,
                                        winRate,
                                        totalOpportunities: filteredUserOpps.length,
                                        wonOpportunities: wonOpps,
                                        lostOpportunities: lostOpps,
                                        activeOpportunities: activeUserOpps.length
                                    });
                                    setLoading(false);
                                    return;
                                } else {
                                    // Admin user - use cache as-is
                                    setSummaryData(data.summary);
                                    setLoading(false);
                                    return;
                                }
                            } else {
                                console.log('[ReportsDashboard] Cache is stale or missing summary, fetching live data...');
                            }
                        } else {
                            console.log('[ReportsDashboard] No cache found, fetching live data...');
                        }
                    } catch (cacheError) {
                        console.error('[ReportsDashboard] Error reading cache:', cacheError);
                        console.log('[ReportsDashboard] Falling back to live data due to cache error');
                    }
                } else {
                    console.log('[ReportsDashboard] Skipping cache (non-admin or custom range), fetching live data...');
                }

                // Fallback: calculate from live data
                const { start, end } = getDateRange(range, customStart, customEnd);
                
                // Use date_internal (YYYY-MM-DD, indexed) for range-filtered queries.
                // Non-admin users on standard ranges skip the cache path (cache only stores
                // top-100 orders by $), so we MUST query with a date filter here —
                // fetching the full orders collection would timeout on large datasets.
                const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                const startStr = toDateStr(start);
                const endStr = toDateStr(end);

                const ordersQuery = query(
                    collection(db, collectionPath('orders')),
                    where('date_internal', '>=', startStr),
                    where('date_internal', '<=', endStr),
                    orderBy('date_internal', 'desc'),
                    limit(5000)
                );

                const [ordersSnap, oppsSnap] = await Promise.all([
                    getDocs(ordersQuery),
                    getDocs(collection(db, collectionPath('opportunities')))
                ]);

                let orders = ordersSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                let opportunities = oppsSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                
                // RBAC: Filter data for non-admin users BEFORE date filtering
                // Wait for userRole to load before filtering
                console.log('[ReportsDashboard] RBAC filtering:', {
                    userRole,
                    totalOrders: orders.length,
                    totalOpps: opportunities.length,
                    isAdmin: canViewAllData(user)
                });
                
                if (!canViewAllReports(user) && !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && user) {
                    const matchSet = new Set(getRbacMatchNames(user));
                    const beforeOrdersFilter = orders.length;
                    const beforeOppsFilter = opportunities.length;
                    
                    // Filter orders by commissions_displayNames (case-insensitive multi-name match).
                    orders = orders.filter(order =>
                        order.commissions_displayNames?.some(name => matchSet.has(String(name || '').toLowerCase().trim()))
                    );
                    
                    // Filter opportunities by uid (assigned user)
                    opportunities = opportunities.filter(opp => opp.uid === user.uid);
                    
                    console.log('[ReportsDashboard] RBAC filtered:', {
                        orders: { before: beforeOrdersFilter, after: orders.length },
                        opportunities: { before: beforeOppsFilter, after: opportunities.length },
                        matchNames: [...matchSet],
                        userUid: user.uid
                    });
                }

                // Filter by date range — date_internal query already scoped the result,
                // but we still check creation_time fallback for any docs missing date_internal.
                const filteredOrders = orders.filter(order => {
                    // If date_internal is present it was already server-filtered; include as-is.
                    if (order.date_internal) return true;
                    const orderDate = order.creation_time 
                        ? new Date(order.creation_time.replace(' ', 'T'))
                        : order.createdAt?.toDate?.()
                            ? order.createdAt.toDate() 
                            : null;
                    
                    if (!orderDate) return false;
                    return orderDate >= start && orderDate <= end;
                });

                const filteredOpps = opportunities.filter(opp => {
                    // Use createdAt (preferred) with startDate as fallback
                    const dateField = opp.createdAt || opp.startDate;
                    const oppDate = dateField?.toDate?.() 
                        ? dateField.toDate() 
                        : dateField 
                            ? new Date(dateField)
                            : null;
                    
                    if (!oppDate || isNaN(oppDate.getTime())) return false;
                    return oppDate >= start && oppDate <= end;
                });

                // Calculate summary
                const assignedOrders = filteredOrders
                    .filter(order => Array.isArray(order.commissions_displayNames) && order.commissions_displayNames.length > 0);

                const totalRevenue = assignedOrders
                    .reduce((sum, order) => sum + (Number(order.amount) || 0), 0);

                const totalOrders = assignedOrders.length;

                const activeOpps = filteredOpps.filter(opp => !['Closed Won', 'Closed Lost'].includes(opp.stage));
                const pipelineValue = activeOpps
                    .reduce((sum, opp) => sum + getValueRangeMidpoint(opp.value), 0);

                const wonOpps = filteredOpps.filter(opp => opp.stage === 'Closed Won').length;
                const lostOpps = filteredOpps.filter(opp => opp.stage === 'Closed Lost').length;
                const closedOpps = wonOpps + lostOpps;
                const winRate = closedOpps > 0 ? (wonOpps / closedOpps) : 0; // Store as decimal (0.0 to 1.0)

                setSummaryData({
                    totalRevenue,
                    totalOrders,
                    pipelineValue,
                    winRate,
                    totalOpportunities: filteredOpps.length,
                    wonOpportunities: wonOpps,
                    lostOpportunities: lostOpps,
                    activeOpportunities: activeOpps.length
                });

                setLoading(false);
                fetchInProgressRef.current = false;
            } catch (error) {
                console.error('[ReportsDashboard] Error fetching data:', error);
                setToast({ message: `Error loading summary: ${error.message}`, type: 'error' });
                setLoading(false);
                fetchInProgressRef.current = false;
            }
        };

        fetchSummaryData();
        
        // Cleanup: allow new fetch if deps change while one is in progress
        return () => {
            fetchInProgressRef.current = false;
        };
    }, [user?.uid, user?.displayName, range, customStart, customEnd, userRole, can]); // Use user.uid instead of user object

    const handleExport = (format) => {
        if (!summaryData) return;
        const headers = ['Metric', 'Value'];
        const dateLabel = range === 'ytd' ? 'YTD' : `${range}d`;
        const data = [
            { metric: 'Total Revenue', value: `$${(summaryData.totalRevenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
            { metric: 'Total Orders', value: (summaryData.totalOrders || 0).toLocaleString() },
            { metric: 'Pipeline Value', value: `$${(summaryData.pipelineValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
            { metric: 'Opportunities Created', value: (summaryData.totalOpportunities || 0).toLocaleString() },
            { metric: 'Revenue Forecast', value: `$${(summaryData.totalForecast || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
            { metric: 'Date Range', value: dateLabel }
        ];
        const rowMapper = (row) => [row.metric, row.value];
        exportData({
            filename: `reports_dashboard_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
            format,
            headers,
            data,
            rowMapper,
            options: format.toLowerCase() === 'pdf' ? {
                title: `Reports Dashboard Summary (${dateLabel})`,
                fontSize: 10,
                orientation: 'portrait',
                pageSize: 'a4'
            } : {
                title: `Reports Dashboard Summary (${dateLabel})`,
                sheetName: 'Dashboard Summary',
                columnWidths: [25, 20]
            }
        });
    };

    if (loading) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading dashboard...</div>
                </div>
            </main>
        );
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}

            {/* Cache Refresh Progress Overlay */}
            {cacheRefreshing && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
                        <div className="text-center">
                            <div className="mb-4">
                                <svg className="animate-spin h-12 w-12 text-indigo-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Refreshing All Caches</h3>
                            <p className="text-gray-600 mb-4">
                                Generating reports for all date ranges...
                            </p>
                            <div className="mb-2">
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div 
                                        className="bg-indigo-600 h-2.5 rounded-full transition-all duration-1000" 
                                        style={{ width: `${refreshProgress}%` }}
                                    ></div>
                                </div>
                            </div>
                            <p className="text-sm text-gray-500">
                                {refreshProgress < 30 
                                    ? `${refreshProgress}s - Fetching data from Firestore...` 
                                    : refreshProgress < 60
                                    ? `${refreshProgress}s - Processing order and opportunity data...`
                                    : refreshProgress < 90
                                    ? `${refreshProgress}s - Generating analytics and caches...`
                                    : 'Finalizing cache generation...'}
                            </p>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="mb-8 flex items-start justify-between">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Reports & Analytics</h2>
                    <p className="text-gray-600">Comprehensive insights into sales performance, pipeline health, and team productivity</p>
                    {cacheAge !== null && (
                        <p className="text-sm text-gray-500 mt-1">
                            Caches last updated {cacheAge < 60 ? `${cacheAge}m` : `${Math.round(cacheAge / 60)}h`} ago
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {hasPermission(user, FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH) && (
                        <Tooltip text="Refresh all report caches (summary, comparison, inactivity)">
                            <button
                                onClick={handleRefreshCache}
                                disabled={cacheRefreshing}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                            >
                                <svg className={`w-4 h-4 ${cacheRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                {cacheRefreshing ? 'Refreshing...' : 'Refresh All Caches'}
                            </button>
                        </Tooltip>
                    )}
                    {can(FEATURE_PERMISSIONS.REPORTS_EXPORT) && (
                        <ExportButton
                            onExport={handleExport}
                            formats={['csv', 'xlsx', 'pdf']}
                            disabled={!summaryData}
                            tooltipText="Export dashboard summary"
                            label="Export"
                            className="bg-indigo-600 hover:bg-indigo-700"
                        />
                    )}
                </div>
            </div>

            {/* Date Range Selector */}
            <div className="mb-6">
                <DateRangeSelector
                    range={range}
                    setRange={setRange}
                    customStart={customStart}
                    setCustomStart={setCustomStart}
                    customEnd={customEnd}
                    setCustomEnd={setCustomEnd}
                    requireApply={true}
                    defaultRange="14"
                />
            </div>

            {/* Summary Cards */}
            <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Performance Summary ({range === 'ytd' ? 'Year to Date' : `Last ${range} Days`})</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                    <Tooltip text={`Total revenue from all orders (${range === 'ytd' ? 'YTD' : `Last ${range} Days`})`}>
                        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow">
                            <div className="text-sm text-gray-600 mb-1">Total Revenue</div>
                            <div className="text-3xl font-bold text-green-600">
                                ${summaryData ? Math.round(summaryData.totalRevenue).toLocaleString('en-US') : '0'}
                            </div>
                        </div>
                    </Tooltip>

                    <Tooltip text={`Number of orders placed (${range === 'ytd' ? 'YTD' : `Last ${range} Days`})`}>
                        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow">
                            <div className="text-sm text-gray-600 mb-1">Total Orders</div>
                            <div className="text-3xl font-bold text-blue-600">
                                {summaryData ? summaryData.totalOrders.toLocaleString() : '0'}
                            </div>
                        </div>
                    </Tooltip>

                    <Tooltip text="Total value of active opportunities in pipeline">
                        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow">
                            <div className="text-sm text-gray-600 mb-1">Pipeline Value</div>
                            <div className="text-3xl font-bold text-purple-600">
                                ${summaryData ? Math.round(summaryData.pipelineValue).toLocaleString('en-US') : '0'}
                            </div>
                        </div>
                    </Tooltip>

                    <Tooltip text={`Total opportunities created (${range === 'ytd' ? 'YTD' : `Last ${range} Days`})`}>
                        <Link to="/reports/opportunities">
                            <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer">
                                <div className="text-sm text-gray-600 mb-1">Opportunities Created</div>
                                <div className="text-3xl font-bold text-amber-600">
                                    {summaryData?.totalOpportunities ?? 0}
                                </div>
                            </div>
                        </Link>
                    </Tooltip>

                    <Tooltip text={`Percentage of closed opportunities that were won (${range === 'ytd' ? 'YTD' : `Last ${range} Days`})`}>
                        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow">
                            <div className="text-sm text-gray-600 mb-1">Win Rate</div>
                            <div className="text-3xl font-bold text-amber-600">
                                {summaryData ? Math.round(summaryData.winRate * 100) : 0}%
                            </div>
                        </div>
                    </Tooltip>
                </div>
            </div>

            {/* Report Categories */}
            <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Report Categories</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                    {/* Sales Rep Reports */}
                    <Tooltip text="View detailed sales rep performance metrics, rankings, and revenue trends">
                        <Link to="/reports/reps">
                            <div className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all duration-200 border-t-4 border-indigo-500 cursor-pointer group h-full">
                                <div className="flex flex-col h-full">
                                    <div className="flex-shrink-0 mb-4">
                                        <svg className="w-12 h-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Sales Rep Reports</h4>
                                    <p className="text-sm text-gray-600 mb-4 flex-grow">Performance metrics and rankings for sales representatives</p>
                                    <div className="text-indigo-600 font-medium flex items-center group-hover:translate-x-1 transition-transform">
                                        View Reports
                                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </Tooltip>

                    {/* Opportunity Reports */}
                    <Tooltip text="Analyze pipeline value, conversion rates, and win probability">
                        <Link to="/reports/opportunities">
                            <div className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all duration-200 border-t-4 border-amber-500 cursor-pointer group h-full">
                                <div className="flex flex-col h-full">
                                    <div className="flex-shrink-0 mb-4">
                                        <svg className="w-12 h-12 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Opportunity Reports</h4>
                                    <p className="text-sm text-gray-600 mb-4 flex-grow">Pipeline analysis and conversion metrics</p>
                                    <div className="text-amber-600 font-medium flex items-center group-hover:translate-x-1 transition-transform">
                                        View Reports
                                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </Tooltip>

                    {/* Customer Reports */}
                    <Tooltip text="Analyze customer performance by revenue and focus area">
                        <Link to="/reports/customers">
                            <div className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all duration-200 border-t-4 border-emerald-500 cursor-pointer group h-full">
                                <div className="flex flex-col h-full">
                                    <div className="flex-shrink-0 mb-4">
                                        <svg className="w-12 h-12 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Customer Reports</h4>
                                    <p className="text-sm text-gray-600 mb-4 flex-grow">Top performers by revenue and focus area</p>
                                    <div className="text-emerald-600 font-medium flex items-center group-hover:translate-x-1 transition-transform">
                                        View Reports
                                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </Tooltip>

                    {/* Order Reports */}
                    <Tooltip text="Track order status, shipment progress, and revenue trends">
                        <Link to="/reports/orders">
                            <div className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all duration-200 border-t-4 border-blue-500 cursor-pointer group h-full">
                                <div className="flex flex-col h-full">
                                    <div className="flex-shrink-0 mb-4">
                                        <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17" stroke="#038cb6ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                            <circle cx="9" cy="19" r="2" fill="#038cb6ff"/>
                                            <circle cx="17" cy="19" r="2" fill="#038cb6ff"/>
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Order Reports</h4>
                                    <p className="text-sm text-gray-600 mb-4 flex-grow">Order status tracking and fulfillment trends</p>
                                    <div className="text-blue-600 font-medium flex items-center group-hover:translate-x-1 transition-transform">
                                        View Reports
                                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </Tooltip>

                    {/* Task Reports */}
                    <Tooltip text="Analyze task completion rates and team productivity">
                        <Link to="/reports/tasks">
                            <div className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all duration-200 border-t-4 border-purple-500 cursor-pointer group h-full">
                                <div className="flex flex-col h-full">
                                    <div className="flex-shrink-0 mb-4">
                                        <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Task Reports</h4>
                                    <p className="text-sm text-gray-600 mb-4 flex-grow">Task completion and productivity metrics</p>
                                    <div className="text-purple-600 font-medium flex items-center group-hover:translate-x-1 transition-transform">
                                        View Reports
                                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </Tooltip>

                    {/* Customer Inactivity Report */}
                    <Tooltip text="Identify active customers with no orders in 365 days">
                        <Link to="/reports/inactivity">
                            <div className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all duration-200 border-t-4 border-red-500 cursor-pointer group h-full">
                                <div className="flex flex-col h-full">
                                    <div className="flex-shrink-0 mb-4">
                                        <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Customer Inactivity</h4>
                                    <p className="text-sm text-gray-600 mb-4 flex-grow">Active customers with no orders in 365 days</p>
                                    <div className="text-red-600 font-medium flex items-center group-hover:translate-x-1 transition-transform">
                                        View Report
                                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </Tooltip>

                    {/* Comparison Reports */}
                    <Tooltip text="Compare performance across time periods (YoY, MoM, QoQ, Custom)">
                        <Link to="/reports/comparison">
                            <div className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all duration-200 border-t-4 border-teal-500 cursor-pointer group h-full">
                                <div className="flex flex-col h-full">
                                    <div className="flex-shrink-0 mb-4">
                                        <svg className="w-12 h-12 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Comparison Reports</h4>
                                    <p className="text-sm text-gray-600 mb-4 flex-grow">Compare performance across time periods</p>
                                    <div className="text-teal-600 font-medium flex items-center group-hover:translate-x-1 transition-transform">
                                        View Reports
                                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </Tooltip>

                    {/* Report Subscriptions */}
                    <Tooltip text="Manage your email report subscriptions and delivery schedules">
                        <Link to="/profile?tab=subscriptions">
                            <div className="bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all duration-200 border-t-4 border-pink-500 cursor-pointer group h-full">
                                <div className="flex flex-col h-full">
                                    <div className="flex-shrink-0 mb-4">
                                        <svg className="w-12 h-12 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Report Subscriptions</h4>
                                    <p className="text-sm text-gray-600 mb-4 flex-grow">Manage email report delivery schedules</p>
                                    <div className="text-pink-600 font-medium flex items-center group-hover:translate-x-1 transition-transform">
                                        Manage Subscriptions
                                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </Tooltip>
                </div>
            </div>
        </main>
    );
};

export default ReportsDashboard;


